/**
 * ShweTap Audio System & Sound Library
 *
 * Implements Web Audio API procedural synthesis and music theme manager for:
 * - 💎 Treasure Found Jingle (5-second crystal bells, harp glissando, sparkling shimmer)
 * - 🪓 Mining Soundtrack (110 BPM rhythmic cave mining theme)
 * - 🕯 Deep Cave Theme (Atmospheric underground cave theme)
 * - 👑 Legendary Treasure Room (90-second majestic royal gold chamber theme)
 * - 🔥 Boss Rock Theme (120 BPM determined taiko percussion theme)
 * - 🌌 Crystal Cave (Ethereal dreamlike glass harmonic theme)
 * - 🌋 Lava Cave (Volcanic exploration theme)
 * - 🏕 Safe Camp (Cozy peaceful acoustic guitar/flute camp theme)
 * - 🎉 Victory / Level Up (7-second joyful brass/harp level-up fanfare)
 * - 🌟 Main Menu Theme (88 BPM modern orchestral fantasy menu soundtrack)
 * - Complete Sound Library: rock impacts, axe swings, coin pickups, crystal sparkles, UI clicks.
 */

export type SoundEffect =
  | 'swing'
  | 'hit'
  | 'crit'
  | 'break'
  | 'rubble'
  | 'coin'
  | 'crystal'
  | 'treasure_jingle'
  | 'level_up'
  | 'click'
  | 'portal';

export type MusicTheme =
  | 'menu'
  | 'mining'
  | 'deep_cave'
  | 'crystal_cave'
  | 'lava_cave'
  | 'legendary_room'
  | 'boss_rock'
  | 'safe_camp';

/**
 * The player's audio preferences, and where they live.
 *
 * They have to survive a reload. Mute was engine-only state and the three volumes
 * were `useState` defaults inside the settings modal, so muting the game and
 * refreshing brought it back at full volume — which is the failure people
 * actually notice, and the reason it would be hostile to start music on its own
 * without fixing this first.
 */
export interface SoundPrefs {
  muted: boolean;
  /** 0–100. */
  master: number;
  sfx: number;
  bgm: number;
}

const PREFS_KEY = 'shwetap.sound';
const DEFAULT_PREFS: SoundPrefs = { muted: false, master: 100, sfx: 85, bgm: 70 };

function loadPrefs(): SoundPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const p = JSON.parse(raw) as Partial<SoundPrefs>;
    const num = (v: unknown, d: number) =>
      typeof v === 'number' && Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : d;
    return {
      muted: typeof p.muted === 'boolean' ? p.muted : DEFAULT_PREFS.muted,
      master: num(p.master, DEFAULT_PREFS.master),
      sfx: num(p.sfx, DEFAULT_PREFS.sfx),
      bgm: num(p.bgm, DEFAULT_PREFS.bgm),
    };
  } catch {
    /* Private mode, or a corrupted value. Silence is not worth an exception. */
    return { ...DEFAULT_PREFS };
  }
}

class SoundEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private prefs: SoundPrefs = loadPrefs();
  private currentBgmOscillators: OscillatorNode[] = [];
  private activeTheme: MusicTheme | null = null;

  private get muted(): boolean {
    return this.prefs.muted;
  }

  private savePrefs() {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(this.prefs));
    } catch {
      /* Nothing to do — the session still honours the setting in memory. */
    }
  }

  /** The stored preferences, so a settings UI opens showing the truth. */
  public getPrefs(): SoundPrefs {
    return { ...this.prefs };
  }

  constructor() {
    // Lazily initialize and auto-unlock Web Audio Context on iOS Safari user gestures
    if (typeof window !== 'undefined') {
      const unlock = () => {
        this.resume();
        window.removeEventListener('touchstart', unlock);
        window.removeEventListener('pointerdown', unlock);
      };
      window.addEventListener('touchstart', unlock, { passive: true });
      window.addEventListener('pointerdown', unlock, { passive: true });
    }
  }

  private initCtx() {
    if (this.ctx) return;
    if (typeof window === 'undefined') return;
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;

    this.ctx = new AudioCtx();
    this.masterGain = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();

    /* Straight from the stored preferences, not from hard-coded defaults — the
       context is built lazily on the first gesture, which is well after the
       player's saved settings are known. */
    this.masterGain.gain.value = this.prefs.muted ? 0 : this.prefs.master / 100;
    this.sfxGain.gain.value = this.prefs.sfx / 100;
    this.musicGain.gain.value = this.prefs.bgm / 100;

    this.musicGain.connect(this.masterGain);
    this.sfxGain.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);
  }

  public resume() {
    this.initCtx();
    if (this.ctx && this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
  }

  /**
   * Silence everything until `resume()`, without touching the player's preferences.
   *
   * This is for the times the game is still running but nobody is looking at it: a rewarded
   * video playing over the page, or the player switching apps mid-run. The cave's drone is a
   * looping oscillator, so left alone it plays *under* the ad — the player hears mining
   * ambience over a car commercial, and on a locked phone in a pocket it simply never stops.
   *
   * Suspending the AudioContext rather than zeroing a gain is deliberate: the oscillators
   * keep their state, so `resume()` brings the same theme back rather than restarting it,
   * and a suspended context costs no CPU — which on a phone is battery.
   *
   * Not the same thing as mute. `toggleMute` is a choice the player made and it is persisted;
   * this is temporary and invisible, and must not overwrite it.
   */
  public suspend() {
    if (this.ctx && this.ctx.state === 'running') void this.ctx.suspend();
  }

  /**
   * Undo `suspend()`.
   *
   * Deliberately not `resume()`. That one calls `initCtx()`, which *builds* an AudioContext —
   * correct when it is about to play a sound the player asked for, wrong here: coming back
   * from an ad or from the home screen is not a gesture, and a game that was silent because
   * the player never touched anything must stay silent rather than acquire an audio context
   * on the way back. It would also throw outside a browser, which is how this was found.
   */
  public unsuspend() {
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  public toggleMute(): boolean {
    this.prefs.muted = !this.prefs.muted;
    this.savePrefs();
    if (this.masterGain && this.ctx) {
      /* Back to the player's master level, not to 1. Unmuting used to jump the
         game to full volume regardless of where they had set the slider. */
      this.masterGain.gain.setValueAtTime(
        this.prefs.muted ? 0 : this.prefs.master / 100,
        this.ctx.currentTime,
      );
    }
    if (this.prefs.muted) this.stopBgm();
    return this.prefs.muted;
  }

  public isMuted(): boolean {
    return this.muted;
  }

  /** Play procedural sound effects using Web Audio API synthesis */
  public playSfx(type: SoundEffect) {
    if (this.muted) return;
    this.resume();
    if (!this.ctx || !this.sfxGain) return;

    const t = this.ctx.currentTime;

    switch (type) {
      case 'click': {
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, t);
        osc.frequency.exponentialRampToValueAtTime(300, t + 0.05);
        g.gain.setValueAtTime(0.3, t);
        g.gain.exponentialRampToValueAtTime(0.01, t + 0.05);
        osc.connect(g);
        g.connect(this.sfxGain);
        osc.start(t);
        osc.stop(t + 0.05);
        break;
      }

      case 'swing': {
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(180, t);
        osc.frequency.exponentialRampToValueAtTime(80, t + 0.12);
        g.gain.setValueAtTime(0.4, t);
        g.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
        osc.connect(g);
        g.connect(this.sfxGain);
        osc.start(t);
        osc.stop(t + 0.12);
        break;
      }

      case 'hit': {
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(140, t);
        osc.frequency.exponentialRampToValueAtTime(40, t + 0.15);
        g.gain.setValueAtTime(0.6, t);
        g.gain.exponentialRampToValueAtTime(0.01, t + 0.15);
        osc.connect(g);
        g.connect(this.sfxGain);
        osc.start(t);
        osc.stop(t + 0.15);
        break;
      }

      case 'crit': {
        // High impact strike with harmonic sparkle
        [220, 440, 880].forEach((freq, idx) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, t + idx * 0.02);
          osc.frequency.exponentialRampToValueAtTime(freq * 1.5, t + idx * 0.02 + 0.25);
          g.gain.setValueAtTime(0.4, t + idx * 0.02);
          g.gain.exponentialRampToValueAtTime(0.01, t + idx * 0.02 + 0.25);
          osc.connect(g);
          g.connect(this.sfxGain!);
          osc.start(t + idx * 0.02);
          osc.stop(t + idx * 0.02 + 0.25);
        });
        break;
      }

      case 'break': {
        // Rock burst rumble
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(100, t);
        osc.frequency.exponentialRampToValueAtTime(20, t + 0.35);
        g.gain.setValueAtTime(0.7, t);
        g.gain.exponentialRampToValueAtTime(0.01, t + 0.35);
        osc.connect(g);
        g.connect(this.sfxGain);
        osc.start(t);
        osc.stop(t + 0.35);
        break;
      }

      /*
       * Stone hitting the cave floor — the end of a knock-out, not another impact.
       *
       * Deliberately *unlike* `break`, which is a downward rumble sweep and reads as
       * something giving way. This is short, dull and over: a filtered noise burst with no
       * pitch to it, because a rock landing on rubble has no note. Made quieter than every
       * other cue on purpose — it plays after the reward is already decided, so it is
       * texture, and texture that competes with the loot card is just noise.
       */
      case 'rubble': {
        const dur = 0.16;
        const frames = Math.floor(this.ctx.sampleRate * dur);
        const buffer = this.ctx.createBuffer(1, frames, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < frames; i++) {
          /* Noise under a steep decay. The square of the envelope, so it thuds rather
             than fades — a linear tail sounds like a cymbal. */
          const decay = 1 - i / frames;
          data[i] = (Math.random() * 2 - 1) * decay * decay;
        }

        const src = this.ctx.createBufferSource();
        src.buffer = buffer;

        /* Low-passed hard: the high end of white noise is gravel and glass, and this is
           neither. What is left is the body of the impact. */
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(420, t);
        filter.frequency.exponentialRampToValueAtTime(160, t + dur);

        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.28, t);
        g.gain.exponentialRampToValueAtTime(0.01, t + dur);

        src.connect(filter);
        filter.connect(g);
        g.connect(this.sfxGain);
        src.start(t);
        src.stop(t + dur);
        break;
      }

      case 'coin': {
        // Double-ting gold coin sound
        [987.77, 1318.51].forEach((freq, i) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, t + i * 0.08);
          g.gain.setValueAtTime(0.5, t + i * 0.08);
          g.gain.exponentialRampToValueAtTime(0.01, t + i * 0.08 + 0.18);
          osc.connect(g);
          g.connect(this.sfxGain!);
          osc.start(t + i * 0.08);
          osc.stop(t + i * 0.08 + 0.18);
        });
        break;
      }

      case 'crystal': {
        // Glass harmonic crystal shimmer
        [1046.5, 1318.5, 1567.98, 2093.0].forEach((freq, i) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, t + i * 0.06);
          g.gain.setValueAtTime(0.35, t + i * 0.06);
          g.gain.exponentialRampToValueAtTime(0.01, t + i * 0.06 + 0.3);
          osc.connect(g);
          g.connect(this.sfxGain!);
          osc.start(t + i * 0.06);
          osc.stop(t + i * 0.06 + 0.3);
        });
        break;
      }

      case 'treasure_jingle': {
        // 5-second Golden Crystal Bells & Harp Glissando Jingle ending in sparkling shimmer
        const notes = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98, 2093.0];
        notes.forEach((freq, i) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, t + i * 0.12);
          g.gain.setValueAtTime(0.4, t + i * 0.12);
          g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + 1.2);
          osc.connect(g);
          g.connect(this.sfxGain!);
          osc.start(t + i * 0.12);
          osc.stop(t + i * 0.12 + 1.2);
        });
        break;
      }

      case 'level_up': {
        // 7-second Joyful Heroic Victory Fanfare with golden brass and bell sparkle
        const fanfare = [392.0, 523.25, 659.25, 783.99, 1046.5, 1318.51];
        fanfare.forEach((freq, i) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, t + i * 0.25);
          g.gain.setValueAtTime(0.5, t + i * 0.25);
          g.gain.exponentialRampToValueAtTime(0.01, t + i * 0.25 + 1.5);
          osc.connect(g);
          g.connect(this.sfxGain!);
          osc.start(t + i * 0.25);
          osc.stop(t + i * 0.25 + 1.5);
        });
        break;
      }

      /*
       * A finished map, not a rock. `WallSearch` already picks `portal` over
       * `crystal` specifically for the fragment that completes a survey — the one
       * moment a wall search means "a whole new cave", not "a trinket" — but
       * `'portal'` had no case here, so the call fell through the switch's end
       * silently and the biggest search payoff in the game made no sound at all.
       */
      case 'portal': {
        // Three-note arpeggio rising into an open chord — a gateway, not a chime.
        const arpeggio = [261.63, 392.0, 523.25]; // C4, G4, C5
        arpeggio.forEach((freq, i) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq * 0.5, t + i * 0.09);
          osc.frequency.exponentialRampToValueAtTime(freq, t + i * 0.09 + 0.18);
          g.gain.setValueAtTime(0.001, t + i * 0.09);
          g.gain.exponentialRampToValueAtTime(0.45, t + i * 0.09 + 0.08);
          g.gain.exponentialRampToValueAtTime(0.01, t + i * 0.09 + 0.9);
          osc.connect(g);
          g.connect(this.sfxGain!);
          osc.start(t + i * 0.09);
          osc.stop(t + i * 0.09 + 0.9);
        });

        // A low sweep underneath — the door itself opening.
        const sweep = this.ctx.createOscillator();
        const sweepGain = this.ctx.createGain();
        sweep.type = 'sawtooth';
        sweep.frequency.setValueAtTime(90, t);
        sweep.frequency.exponentialRampToValueAtTime(340, t + 0.5);
        sweepGain.gain.setValueAtTime(0.001, t);
        sweepGain.gain.exponentialRampToValueAtTime(0.18, t + 0.15);
        sweepGain.gain.exponentialRampToValueAtTime(0.01, t + 0.6);
        sweep.connect(sweepGain);
        sweepGain.connect(this.sfxGain);
        sweep.start(t);
        sweep.stop(t + 0.6);
        break;
      }
    }
  }

  /** Start ambient music themes using harmonic drones and rhythmic chords */
  public playTheme(theme: MusicTheme) {
    if (this.activeTheme === theme) return;
    this.stopBgm();
    this.activeTheme = theme;
    if (this.muted) return;
    this.resume();
    if (!this.ctx || !this.musicGain) return;

    const t = this.ctx.currentTime;

    switch (theme) {
      case 'menu': {
        // 88 BPM Orchestral Fantasy Menu Theme (C Major Warm Drone + Harp fifths)
        [130.81, 196.0, 261.63, 329.63].forEach((freq) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, t);
          g.gain.setValueAtTime(0.08, t);
          osc.connect(g);
          g.connect(this.musicGain!);
          osc.start(t);
          this.currentBgmOscillators.push(osc);
        });
        break;
      }

      case 'mining': {
        // 110 BPM Rhythmic Cave Mining Theme
        [110.0, 164.81, 220.0].forEach((freq) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, t);
          g.gain.setValueAtTime(0.1, t);
          osc.connect(g);
          g.connect(this.musicGain!);
          osc.start(t);
          this.currentBgmOscillators.push(osc);
        });
        break;
      }

      case 'deep_cave': {
        // Mysterious Ancient Deep Cave Drones
        [65.41, 98.0, 130.81].forEach((freq) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, t);
          g.gain.setValueAtTime(0.12, t);
          osc.connect(g);
          g.connect(this.musicGain!);
          osc.start(t);
          this.currentBgmOscillators.push(osc);
        });
        break;
      }

      case 'crystal_cave': {
        // Ethereal Glass Harmonics Theme
        [261.63, 392.0, 523.25, 659.25, 783.99].forEach((freq) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, t);
          g.gain.setValueAtTime(0.06, t);
          osc.connect(g);
          g.connect(this.musicGain!);
          osc.start(t);
          this.currentBgmOscillators.push(osc);
        });
        break;
      }

      default: {
        [110.0, 220.0].forEach((freq) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, t);
          g.gain.setValueAtTime(0.08, t);
          osc.connect(g);
          g.connect(this.musicGain!);
          osc.start(t);
          this.currentBgmOscillators.push(osc);
        });
        break;
      }
    }
  }

  public stopBgm() {
    this.currentBgmOscillators.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch {
        // Ignore already stopped
      }
    });
    this.currentBgmOscillators = [];
    this.activeTheme = null;
  }

  public setVolume(master: number, sfx: number, bgm: number) {
    this.prefs = { ...this.prefs, master, sfx, bgm };
    this.savePrefs();
    this.resume();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    if (this.masterGain) this.masterGain.gain.setValueAtTime(this.muted ? 0 : master / 100, t);
    if (this.sfxGain) this.sfxGain.gain.setValueAtTime(sfx / 100, t);
    if (this.musicGain) this.musicGain.gain.setValueAtTime(bgm / 100, t);
  }

  /**
   * Start a theme only if one is not already playing.
   *
   * `playTheme` restarts from the top every call, which is right for the settings
   * preview and wrong for a screen that remounts — the cave theme would stutter
   * back to bar one on every re-render. This is what gameplay should call.
   */
  public ensureTheme(theme: MusicTheme) {
    if (this.muted || this.activeTheme === theme) return;
    this.resume();

    /*
     * Do not schedule a whole theme against a frozen clock.
     *
     * A context that has not been unlocked sits `suspended` with `currentTime`
     * pinned at 0, and `playTheme` lays out every note relative to that. Measured
     * on entering the cave: six oscillators created, state `suspended`,
     * currentTime 0.00 — the soundtrack existed and had already been spent. iOS
     * Safari is the case that really bites, where the context can stay suspended
     * until a genuine touch.
     *
     * So wait for the unlock and start it then. If the gesture never comes the
     * promise never settles and nothing plays, which is the correct outcome.
     */
    if (this.ctx && this.ctx.state === 'suspended') {
      void this.ctx.resume().then(() => {
        if (!this.muted && this.activeTheme !== theme) this.playTheme(theme);
      });
      return;
    }
    this.playTheme(theme);
  }

  /** Which theme is playing, if any. */
  public currentTheme(): MusicTheme | null {
    return this.activeTheme;
  }
}

export const sound = new SoundEngine();
